<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import type { SolutionDetailProps } from "./SolutionDetailProps.js";
	import { getTenant } from "../getTenant.js";
	import Link from "../components/Link.svelte";
	import { getTranslate } from "../utils/getTranslate";
	import translationsJson from "./SolutionDetail.translations.json";
	import { contactPathMap } from "../../../core/schema/paths/contactPathMap";
	import { referencesPathMap } from "../../../core/schema/paths/referencesPathMap.js";
	import arrowRight from "../../../core/assets/icons/arrow-right.svg?raw";
	import BlogSection from "../components/BlogSection.svelte";
	import { referenceToCard } from "../components/referenceToCard.js";
	import type { CardItem } from "../components/ArticleCardProps.js";
	import Content from "../components/Content.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import spiralImg from "../assets/images/about/spiral.jpg";
	import Icon from "../components/Icon.svelte";
	import bird from "../../../core/assets/icons/bird.svg?raw";
	import zeroOneNumbers from "../../../core/assets/icons/zero-one-numbers.svg?raw";
	import eye from "../../../core/assets/icons/eye.svg?raw";
	import ContactForm from "../components/Contact/ContactForm.svelte";
	import HeadingSection from "../components/HeadingSection.svelte";

	type $$Props = SolutionDetailProps;

	const translate = getTranslate(translationsJson);
	const { solution, references } = $$restProps as $$Props;
	const tenant = getTenant();

	const cards: CardItem[] = references
		.toSorted((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt))
		.slice(0, 3)
		.map(referenceToCard);

	const challengeItems = solution.challenges.items[tenant.locale];
	const { benefits } = solution.whyLogtech;

	const customHeadline = solution.headline[tenant.locale].trim();
	const headline = customHeadline === "" ? solution.name[tenant.locale] : customHeadline;

	const challengesHeadline = solution.challenges.headline[tenant.locale];
	const challengesParagraph = solution.challenges.paragraph[tenant.locale];
	const whyLogtechParagraphs = solution.whyLogtech.intro[tenant.locale]
		.split("\n\n")
		.map((paragraph) => paragraph.trim())
		.filter((paragraph) => paragraph !== "");

	const { bodySections, benefitsList, differentiator } = solution;
	const benefitsListItems = benefitsList.items[tenant.locale];
	const hasDifferentiator =
		differentiator.heading[tenant.locale].trim() !== "" || differentiator.paragraph[tenant.locale].trim() !== "";

	const differentiators = [
		{ icon: bird, titleKey: "differentiatorTitle1", descKey: "differentiatorDesc1" },
		{ icon: zeroOneNumbers, titleKey: "differentiatorTitle2", descKey: "differentiatorDesc2" },
		{ icon: eye, titleKey: "differentiatorTitle3", descKey: "differentiatorDesc3" },
		{ icon: eye, titleKey: "differentiatorTitle4", descKey: "differentiatorDesc4" },
		{ icon: eye, titleKey: "differentiatorTitle5", descKey: "differentiatorDesc5" },
	] as const;
</script>

<Layout>
	<div class="container">
		<HeadingSection {headline} tag={solution.name[tenant.locale]}>
			<div class="lg:text-md/normal mb-5 tracking-[0.02em]">
				<Content content={solution.description[tenant.locale]} keepParagraphSize />
			</div>
			<Link
				asButton
				variant="secondary"
				class="md:mt-12 xl:mt-16"
				icon={arrowRight}
				href="/{contactPathMap[tenant.locale]}">{translate("contactUs")}</Link
			>
		</HeadingSection>

		<!-- Challenges -->
		{#if challengeItems.length > 0 || challengesHeadline !== "" || challengesParagraph !== ""}
			<section>
				<div class="mx-4 flex flex-col md:flex-row lg:mx-12 lg:gap-4 xl:mx-32 xl:gap-32">
					<div
						class="relative order-2 -mx-4 mt-8 aspect-[3/2] w-[calc(100%+2rem)] flex-1 overflow-hidden rounded-lg md:order-1 md:-mx-0 md:mt-0 md:size-[19.875rem] md:self-center lg:size-[24.875rem] xl:h-[47.5rem] xl:w-[48rem]"
					>
						<StaticPicture
							image={spiralImg}
							alt={translate("challengesAltText")}
							height={760}
							width={768}
							loading="eager"
							class="absolute inset-0"
							imgClass="absolute inset-0 size-full object-contain md:object-left"
						/>
					</div>

					<div class="text-ltsecondary order-1 flex flex-1 flex-col justify-center md:order-2 xl:justify-center">
						<h2 class="text-ltprimary m-0 mb-8 p-0 uppercase tracking-wider">
							{challengesHeadline}
						</h2>
						<div class="lg:text-md/normal mb-10 tracking-[0.02em] md:mb-12 lg:mb-16">
							{challengesParagraph}
						</div>
						{#if challengeItems.length > 0}
							<div class="mb-0 font-medium uppercase tracking-[0.02em] lg:text-base/normal">
								{translate("challengesOlHeadline")}
							</div>
							<ul class="list-disc pl-6 tracking-[0.02em] lg:text-base/normal">
								{#each challengeItems as challenge}
									<li>{challenge}</li>
								{/each}
							</ul>
						{/if}
					</div>
				</div>
			</section>
		{/if}
	</div>

	<!-- Why Logtech -->
	{#if benefits.length > 0 || whyLogtechParagraphs.length > 0}
		<section class="!mb-0">
			<div class="border-lttertiary border-y">
				<div class="border-lttertiary mx-4 grid grid-cols-1 border-x md:grid-cols-2 lg:mx-12 xl:mx-32">
					<div class="p-8">
						<h2 class="text-ltprimary mb-4 uppercase tracking-[0.02em] xl:mb-8">
							{translate("whyLogtechHeadline")}
						</h2>
						<div class="max-w-[35.75rem]">
							{#each whyLogtechParagraphs as paragraph, index (index)}
								<div class="mb-4 tracking-[0.02em]" class:text-md={index === 0} class:text-base={index !== 0}>
									{paragraph}
								</div>
							{/each}
						</div>
						<Link
							asButton
							variant="secondary"
							class="mt-8 md:mt-12 xl:mt-16"
							icon={arrowRight}
							href="/{contactPathMap[tenant.locale]}">{translate("contactUs")}</Link
						>
					</div>
					<div class="bg-lttertiary-100/70 border-lttertiary md:border-l">
						{#each benefits as benefit}
							<div
								class="border-lttertiary flex flex-col justify-between gap-4 border-b p-8 last:border-b-0 xl:flex-row xl:gap-8"
							>
								<div class="text-2md text-ltprimary-400 font-roboto flex-1 font-normal uppercase tracking-[0.02em]">
									{benefit.headline[tenant.locale]}
								</div>
								<div class="text-ltsecondary flex-1 text-base/[1.6] font-normal">
									{benefit.description[tenant.locale]}
								</div>
							</div>
						{/each}
					</div>
				</div>
			</div>
		</section>
	{/if}

	<!-- Body sections (functional solutions) -->
	{#each bodySections as section (section.heading[tenant.locale])}
		<div class="container">
			<section>
				<div class="mx-4 md:mx-16 xl:mx-32">
					<h2 class="text-ltprimary mb-4 uppercase tracking-[0.02em] xl:mb-8">
						{section.heading[tenant.locale]}
					</h2>
					<div class="text-ltsecondary lg:text-md/normal tracking-[0.02em]">
						{#each section.paragraph[tenant.locale].split("\n\n") as paragraph (paragraph)}
							<p class="mb-4">{paragraph}</p>
						{/each}
					</div>
				</div>
			</section>
		</div>
	{/each}

	<!-- Benefits list (functional solutions) -->
	{#if benefitsListItems.length > 0 || benefitsList.heading[tenant.locale].trim() !== ""}
		<div class="container">
			<section>
				<div class="mx-4 md:mx-16 xl:mx-32">
					<h2 class="text-ltprimary mb-4 uppercase tracking-[0.02em] xl:mb-8">
						{benefitsList.heading[tenant.locale]}
					</h2>
					<ul class="text-ltsecondary list-disc pl-6 tracking-[0.02em] lg:text-base/normal">
						{#each benefitsListItems as item (item)}
							<li>{item}</li>
						{/each}
					</ul>
				</div>
			</section>
		</div>
	{/if}

	<!-- Differentiator (functional solutions) -->
	{#if hasDifferentiator}
		<div class="container">
			<section>
				<div class="mx-4 md:mx-16 xl:mx-32">
					<h2 class="text-ltprimary mb-4 uppercase tracking-[0.02em] xl:mb-8">
						{differentiator.heading[tenant.locale]}
					</h2>
					<div class="text-ltsecondary lg:text-md/normal tracking-[0.02em]">
						{#each differentiator.paragraph[tenant.locale].split("\n\n") as paragraph (paragraph)}
							<p class="mb-4">{paragraph}</p>
						{/each}
					</div>
				</div>
			</section>
		</div>
	{/if}

	<!-- References -->
	{#if cards.length > 0}
		<section class="!mt-0 mb-10">
			<div class="border-lttertiary mb-10 border-b">
				<div class="mx-4 my-10 md:mx-12 xl:mx-32">
					<h2 class="text-ltprimary mb-0 text-center uppercase tracking-[0.02em]">
						{translate("referencesHeadline")}
					</h2>
				</div>
				<BlogSection {cards} />
				<Link
					asButton
					variant="secondary"
					class="mx-auto my-8 md:my-10"
					icon={arrowRight}
					href="/{referencesPathMap[tenant.locale]}">{translate("referencesLink")}</Link
				>
			</div>
		</section>
	{/if}

	<!-- Data-driven solutions -->
	<section class="!mb-0">
		<div class="mx-4 mb-10 text-center md:mx-16 xl:mx-32">
			<h2 class="text-ltprimary mb-4 text-center uppercase tracking-[0.02em]">
				{translate("dataDrivenHeadline")}
			</h2>
			<p class="!text-2md mx-auto mb-0 max-w-[47rem] leading-normal">
				{translate("dataDrivenSubheadline")}
			</p>
		</div>
		<div class="border-lttertiary border-t">
			<div class="mx-4 grid grid-cols-1 md:mx-12 lg:grid-cols-2 xl:mx-32 xl:grid-cols-3">
				{#each differentiators as item, index (index)}
					<div
						class="bg-lttertiary-100 border-lttertiary before:bg-lttertiary/60 relative isolate overflow-hidden border-x p-4 before:absolute before:inset-x-0 before:bottom-0 before:-z-10 before:h-0 before:transition-[height] before:duration-500 before:content-[''] hover:before:h-full md:p-6 lg:border-l-0 lg:border-r lg:p-8 xl:min-h-[30.5rem]"
						class:border-b={index !== differentiators.length - 1}
						class:lg:!border-l={index % 2 === 0}
						class:lg:border-b-0={index >= differentiators.length - (differentiators.length % 2 || 2)}
						class:xl:!border-l={index % 3 === 0}
						class:xl:!border-l-0={index % 2 === 0 && index % 3 !== 0}
						class:xl:border-b-0={index >= differentiators.length - (differentiators.length % 3 || 3)}
					>
						<Icon icon={item.icon} class="text-ltprimary size-14 lg:size-20" />
						<h3 class="text-ltprimary text-2md mb-0 mt-4 font-normal uppercase tracking-[0.02em] md:mt-6 lg:mt-16">
							{translate(item.titleKey)}
						</h3>
						<p class="text-ltsecondary mb-0 mt-4 text-base/[1.6] font-normal">
							{translate(item.descKey)}
						</p>
					</div>
				{/each}
			</div>
		</div>
	</section>

	<section class="!mb-0">
		<ContactForm />
	</section>
</Layout>
